import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Save,
  ChevronDown,
  Loader2,
  Type,
  Sparkles,
  Grid3x3,
  Code,
  FileText,
  MessageCircle,
  ShieldAlert,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";

const ICON_OPTIONS = [
  "Headphones", "Users", "HelpCircle", "BarChart3", "Bot", "Shield",
  "Sparkles", "Zap", "MessageCircle", "Phone", "Package", "Star",
  "Rocket", "Target", "TrendingUp", "Award", "CheckCircle", "Lightbulb",
];

function Section({ title, icon: Icon, defaultOpen = false, children }) {
  return (
    <details
      open={defaultOpen}
      className="group bg-white border border-slate-200 rounded-2xl overflow-hidden"
    >
      <summary className="cursor-pointer list-none px-5 py-4 bg-slate-50 hover:bg-slate-100 flex items-center justify-between transition-colors">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-[#DC2626]/10 text-[#DC2626] flex items-center justify-center">
            <Icon className="w-4 h-4" />
          </div>
          <span className="font-display font-bold text-slate-900 text-sm">{title}</span>
        </div>
        <ChevronDown className="w-4 h-4 text-slate-400 group-open:rotate-180 transition-transform" />
      </summary>
      <div className="p-5 space-y-4 border-t border-slate-100">{children}</div>
    </details>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="text-xs font-medium text-slate-700 mb-1.5 block">{label}</label>
      {children}
      {hint && <div className="text-[11px] text-slate-400 mt-1 leading-relaxed">{hint}</div>}
    </div>
  );
}

function TextInput({ value, onChange, placeholder, testId }) {
  return (
    <input
      type="text"
      data-testid={testId}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
    />
  );
}

function TextArea({ value, onChange, rows = 3, placeholder, testId }) {
  return (
    <textarea
      data-testid={testId}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      rows={rows}
      placeholder={placeholder}
      className="w-full px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-y font-sans"
    />
  );
}

function IconSelect({ value, onChange }) {
  return (
    <select
      value={value || "Bot"}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
    >
      {ICON_OPTIONS.map((n) => (
        <option key={n} value={n}>{n}</option>
      ))}
    </select>
  );
}

export default function SiteSettingsPanel({ currentUser }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const isSuperAdmin = currentUser?.role === "super_admin";

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/site-config");
      setConfig(r.data.config);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal memuat konfigurasi");
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/admin/site-config", config);
      setConfig(r.data.config);
      toast.success("Pengaturan tersimpan. Refresh halaman utama untuk melihat perubahan.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menyimpan pengaturan");
    } finally {
      setSaving(false);
    }
  };

  const update = (key, value) =>
    setConfig((prev) => ({ ...prev, [key]: value }));

  const updateArrayItem = (key, index, value) =>
    setConfig((prev) => {
      const arr = [...(prev[key] || [])];
      arr[index] = value;
      return { ...prev, [key]: arr };
    });

  const updateArrayObjectField = (key, index, field, value) =>
    setConfig((prev) => {
      const arr = [...(prev[key] || [])];
      arr[index] = { ...(arr[index] || {}), [field]: value };
      return { ...prev, [key]: arr };
    });

  useEffect(() => {
    if (isSuperAdmin) load();
    else setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin]);

  if (!isSuperAdmin) {
    return (
      <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center">
        <ShieldAlert className="w-10 h-10 text-slate-300 mx-auto mb-3" />
        <div className="text-sm text-slate-500">
          Hanya <b>Super Admin</b> yang dapat mengakses Pengaturan Tampilan.
        </div>
      </div>
    );
  }

  if (loading || !config) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  const c = config;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-4 pb-32"
    >
      <div className="bg-white border border-slate-200 rounded-2xl p-6 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="font-display font-bold text-lg text-slate-900">
            Pengaturan Tampilan Halaman Depan
          </div>
          <div className="text-sm text-slate-500 mt-0.5 leading-relaxed">
            Ubah konten landing page dan chatbot tanpa perlu edit kode atau redeploy.
            Perubahan tersimpan di database dan aktif setelah refresh halaman.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 text-sm text-slate-600 hover:bg-slate-100 px-3 py-2 rounded-lg transition-colors font-medium"
          >
            Preview ↗
          </a>
          <button
            onClick={load}
            className="flex items-center gap-1.5 text-sm text-slate-600 hover:bg-slate-100 px-3 py-2 rounded-lg transition-colors font-medium"
          >
            <RefreshCw className="w-4 h-4" /> Muat ulang
          </button>
          <button
            data-testid={ADMIN.settingsSaveBtn}
            onClick={save}
            disabled={saving}
            className="bg-[#DC2626] hover:bg-[#B91C1C] disabled:opacity-60 text-white text-sm font-medium py-2 px-4 rounded-lg flex items-center gap-2 transition-colors"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? "Menyimpan..." : "Simpan"}
          </button>
        </div>
      </div>

      <Section title="Branding & Kontak" icon={Type} defaultOpen>
        <Field label="Nama Brand" hint="Muncul di navbar, footer, dan email notifikasi lead.">
          <TextInput
            testId={ADMIN.settingsBrandName}
            value={c.brand_name}
            onChange={(v) => update("brand_name", v)}
          />
        </Field>
        <Field label="Subtitle Navbar" hint="Baris kecil di bawah nama brand.">
          <TextInput value={c.nav_subtitle} onChange={(v) => update("nav_subtitle", v)} />
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Nomor WhatsApp" hint="Contoh: 628113195050 (tanpa + atau spasi). Kosongkan untuk sembunyikan tombol WhatsApp.">
            <TextInput value={c.whatsapp_number} onChange={(v) => update("whatsapp_number", v)} />
          </Field>
          <Field label="Email Notifikasi Lead" hint="Setiap lead baru akan dikirim ke email ini.">
            <TextInput value={c.notify_email} onChange={(v) => update("notify_email", v)} />
          </Field>
        </div>
      </Section>

      <Section title="Hero (Bagian Atas Landing)" icon={Sparkles} defaultOpen>
        <Field label="Badge Kecil di Atas Judul">
          <TextInput value={c.hero_badge} onChange={(v) => update("hero_badge", v)} />
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Field label="Judul (Baris 1)">
            <TextInput
              testId={ADMIN.settingsHeroTitleTop}
              value={c.hero_title_top}
              onChange={(v) => update("hero_title_top", v)}
            />
          </Field>
          <Field label="Kata Sorot (Merah)" hint="Bagian yang di-highlight merah pada baris 2.">
            <TextInput value={c.hero_title_highlight} onChange={(v) => update("hero_title_highlight", v)} />
          </Field>
          <Field label="Lanjutan Baris 2" hint="Teks setelah kata sorot. Awali dengan spasi jika perlu.">
            <TextInput value={c.hero_title_tail} onChange={(v) => update("hero_title_tail", v)} />
          </Field>
        </div>
        <Field label="Deskripsi Paragraf">
          <TextArea value={c.hero_description} onChange={(v) => update("hero_description", v)} rows={4} />
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Teks Tombol CTA Utama">
            <TextInput value={c.hero_cta_primary} onChange={(v) => update("hero_cta_primary", v)} />
          </Field>
          <Field label="Teks Tombol CTA Sekunder">
            <TextInput value={c.hero_cta_secondary} onChange={(v) => update("hero_cta_secondary", v)} />
          </Field>
        </div>
        <Field label="Statistik Kecil (4 slot)" hint="Angka + label singkat, ditampilkan di bawah CTA.">
          <div className="space-y-2">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="grid grid-cols-3 gap-2">
                <TextInput
                  value={c.hero_stats?.[i]?.value}
                  onChange={(v) => updateArrayObjectField("hero_stats", i, "value", v)}
                  placeholder={`Nilai #${i + 1}`}
                />
                <div className="col-span-2">
                  <TextInput
                    value={c.hero_stats?.[i]?.label}
                    onChange={(v) => updateArrayObjectField("hero_stats", i, "label", v)}
                    placeholder={`Label #${i + 1}`}
                  />
                </div>
              </div>
            ))}
          </div>
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Kicker Visual" hint="Uppercase kecil di kartu merah kanan.">
            <TextInput value={c.hero_visual_kicker} onChange={(v) => update("hero_visual_kicker", v)} />
          </Field>
          <Field label="Judul Visual" hint="Kalimat besar di kartu merah kanan.">
            <TextInput value={c.hero_visual_title} onChange={(v) => update("hero_visual_title", v)} />
          </Field>
        </div>
        <Field label="Mini Card 'Lead Baru' (nyempil di sudut kiri kartu)" hint="Kosongkan nama untuk menyembunyikan kartu ini.">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <TextInput
              value={c.hero_minicard_name}
              onChange={(v) => update("hero_minicard_name", v)}
              placeholder="Nama contoh"
            />
            <TextInput
              value={c.hero_minicard_desc}
              onChange={(v) => update("hero_minicard_desc", v)}
              placeholder="Deskripsi kebutuhan"
            />
            <TextInput
              value={c.hero_minicard_time}
              onChange={(v) => update("hero_minicard_time", v)}
              placeholder="Waktu (contoh: 2 menit lalu)"
            />
          </div>
        </Field>
      </Section>

      <Section title="Kartu Kegunaan (4 Slot)" icon={Grid3x3} defaultOpen>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Kicker Section">
            <TextInput value={c.usecases_kicker} onChange={(v) => update("usecases_kicker", v)} />
          </Field>
          <Field label="Judul Section">
            <TextInput value={c.usecases_title} onChange={(v) => update("usecases_title", v)} />
          </Field>
        </div>
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="border border-slate-200 rounded-xl p-4 space-y-3 bg-slate-50/50">
            <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider">
              Kartu #{i + 1}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Field label="Ikon">
                <IconSelect
                  value={c.usecases?.[i]?.icon}
                  onChange={(v) => updateArrayObjectField("usecases", i, "icon", v)}
                />
              </Field>
              <div className="sm:col-span-2">
                <Field label="Judul">
                  <TextInput
                    value={c.usecases?.[i]?.title}
                    onChange={(v) => updateArrayObjectField("usecases", i, "title", v)}
                  />
                </Field>
              </div>
            </div>
            <Field label="Deskripsi">
              <TextArea
                value={c.usecases?.[i]?.desc}
                onChange={(v) => updateArrayObjectField("usecases", i, "desc", v)}
                rows={3}
              />
            </Field>
          </div>
        ))}
      </Section>

      <Section title="Section Embed" icon={Code} defaultOpen>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Kicker">
            <TextInput value={c.embed_kicker} onChange={(v) => update("embed_kicker", v)} />
          </Field>
          <Field label="Judul">
            <TextInput value={c.embed_title} onChange={(v) => update("embed_title", v)} />
          </Field>
        </div>
        <Field label="Deskripsi">
          <TextArea value={c.embed_description} onChange={(v) => update("embed_description", v)} rows={3} />
        </Field>
        <Field label="Fitur (4 slot)" hint="Poin-poin bulet centang di bawah deskripsi.">
          <div className="space-y-2">
            {[0, 1, 2, 3].map((i) => (
              <TextInput
                key={i}
                value={c.embed_features?.[i]}
                onChange={(v) => updateArrayItem("embed_features", i, v)}
                placeholder={`Fitur #${i + 1}`}
              />
            ))}
          </div>
        </Field>
        <Field label="Catatan Info (kotak merah)">
          <TextArea value={c.embed_note} onChange={(v) => update("embed_note", v)} rows={2} />
        </Field>
      </Section>

      <Section title="Footer" icon={FileText} defaultOpen>
        <Field label="Tagline Footer">
          <TextInput value={c.footer_tagline} onChange={(v) => update("footer_tagline", v)} />
        </Field>
        <Field
          label="Copyright"
          hint="Gunakan {year} untuk tahun otomatis. Contoh: © {year} Nama. All rights reserved."
        >
          <TextInput value={c.footer_copyright} onChange={(v) => update("footer_copyright", v)} />
        </Field>
      </Section>

      <Section title="Chatbot Widget" icon={MessageCircle} defaultOpen>
        <Field label="Nama Chatbot" hint="Muncul di header widget mengambang.">
          <TextInput
            testId={ADMIN.settingsChatbotName}
            value={c.chatbot_name}
            onChange={(v) => update("chatbot_name", v)}
          />
        </Field>
        <Field
          label="Pesan Sapaan Awal"
          hint="Pesan pertama saat pengunjung buka chat. Boleh pakai **teks tebal** dan enter untuk baris baru."
        >
          <TextArea value={c.chatbot_greeting} onChange={(v) => update("chatbot_greeting", v)} rows={5} />
        </Field>
        <Field label="Quick Reply (5 slot)" hint="Tombol pilihan cepat di awal chat.">
          <div className="space-y-2">
            {[0, 1, 2, 3, 4].map((i) => (
              <TextInput
                key={i}
                value={c.chatbot_quick_replies?.[i]}
                onChange={(v) => updateArrayItem("chatbot_quick_replies", i, v)}
                placeholder={`Quick reply #${i + 1}`}
              />
            ))}
          </div>
        </Field>
        <Field
          label="Instruksi AI (System Prompt) — Advanced"
          hint="Aturan yang mengarahkan cara AI menjawab. Kosongkan untuk pakai default (general assistant + aturan harga otomatis lead capture)."
        >
          <TextArea
            value={c.chatbot_system_prompt}
            onChange={(v) => update("chatbot_system_prompt", v)}
            rows={10}
          />
        </Field>
      </Section>

      {/* Sticky save at bottom for long forms */}
      <div className="sticky bottom-4 pt-4 flex justify-end">
        <button
          onClick={save}
          disabled={saving}
          className="bg-[#DC2626] hover:bg-[#B91C1C] disabled:opacity-60 text-white text-sm font-medium py-3 px-6 rounded-xl shadow-lg flex items-center gap-2 transition-colors"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? "Menyimpan..." : "Simpan Semua Perubahan"}
        </button>
      </div>
    </motion.div>
  );
}
